/* eslint-disable react/display-name */
import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import CouponComponent from '../components/Coupon/Coupon'
import CustomLoader from '../components/Loader/CustomLoader'
import DataTable from 'react-data-table-component'
import orderBy from 'lodash/orderBy'
import { getCoupons, deleteCoupon, editCoupon } from '../apollo'
import {
  Box, Container, Typography, Paper, TextField, InputAdornment,
  IconButton, Menu, MenuItem, ListItemIcon, Button, Dialog, DialogTitle,
  DialogContent, Switch, Chip
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import CloseIcon from '@mui/icons-material/Close'

const GET_COUPONS = gql`${getCoupons}`
const EDIT_COUPON = gql`${editCoupon}`
const DELETE_COUPON = gql`${deleteCoupon}`

function ActionMenu({ row, onEdit, onDelete }) {
  const [anchor, setAnchor] = useState(null)
  return (
    <>
      <IconButton size="small" onClick={e => setAnchor(e.currentTarget)} sx={{ color: '#94a3b8', '&:hover': { bgcolor: '#f1f5f9' } }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        PaperProps={{ elevation: 0, sx: { borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #f1f5f9', minWidth: 160 } }}>
        <MenuItem onClick={() => { setAnchor(null); onEdit(row) }}
          sx={{ borderRadius: '8px', mx: 0.5, my: 0.25, fontSize: '0.83rem' }}>
          <ListItemIcon><EditIcon fontSize="small" sx={{ color: '#3b82f6' }} /></ListItemIcon>Edit
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); onDelete(row._id) }}
          sx={{ borderRadius: '8px', mx: 0.5, my: 0.25, fontSize: '0.83rem', color: '#ef4444' }}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>Delete
        </MenuItem>
      </Menu>
    </>
  )
}

function Coupons(props) {
  const { t } = props
  const [editModal, setEditModal] = useState(false)
  const [addModal, setAddModal] = useState(false)
  const [selectedCoupon, setSelectedCoupon] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const { data, error, loading, refetch } = useQuery(GET_COUPONS, {
    variables: { page, rowsPerPage, search: searchQuery.length > 2 ? searchQuery : null },
    fetchPolicy: 'network-only'
  })
  const [mutateEdit] = useMutation(EDIT_COUPON, { refetchQueries: [{ query: GET_COUPONS }] })
  const [mutateDelete] = useMutation(DELETE_COUPON, {
    onCompleted: () => refetch(),
    onError: () => {}
  })

  const handleEdit = (row) => { setSelectedCoupon(row); setEditModal(true) }
  const handleDelete = (id) => { mutateDelete({ variables: { id } }) }

  const coupons = data?.coupons?.coupons ?? []
  const totalCount = data?.coupons?.totalCount ?? 0
  const enabledCount = coupons.filter(c => c.enabled).length

  const columns = [
    {
      name: 'COUPON', sortable: true, selector: row => row.title, minWidth: '200px',
      cell: row => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#fff7ed', border: '1px solid #fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <LocalOfferIcon sx={{ fontSize: 18, color: '#f97316' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', fontFamily: 'monospace', letterSpacing: '0.05em' }}>{row.title}</Typography>
          </Box>
        </Box>
      )
    },
    {
      name: 'DISCOUNT', hide: 'sm', sortable: true, selector: row => row.discount, minWidth: '120px',
      cell: row => (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1.5, py: 0.5, borderRadius: '8px', bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1d4ed8' }}>{row.discount}%</Typography>
        </Box>
      )
    },
    {
      name: 'STATUS', minWidth: '160px',
      cell: row => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Switch size="small" defaultChecked={row.enabled}
            onChange={() => mutateEdit({ variables: { couponInput: { _id: row._id, title: row.title, discount: row.discount, enabled: !row.enabled } } })}
            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#22c55e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#22c55e' } }} />
          <Chip label={row.enabled ? 'Active' : 'Inactive'} size="small"
            sx={{ bgcolor: row.enabled ? '#f0fdf4' : '#f8fafc', color: row.enabled ? '#166534' : '#64748b', border: `1px solid ${row.enabled ? '#bbf7d0' : '#e2e8f0'}`, fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
        </Box>
      )
    },
    { name: '', right: true, width: '60px', cell: row => <ActionMenu row={row} onEdit={handleEdit} onDelete={handleDelete} /> }
  ]

  return (
    <>
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #431407 60%, #c2410c 100%)', px: { xs: 2, md: 4 }, pt: 3.5, pb: 5, position: 'relative', overflow: 'hidden', '&::before': { content: '""', position: 'absolute', top: -60, right: -60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LocalOfferIcon sx={{ color: '#fdba74', fontSize: 26 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#fff', lineHeight: 1.15, letterSpacing: '-0.02em' }}>Coupons</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', mt: 0.3 }}>Manage discount coupons for customers</Typography>
            </Box>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelectedCoupon(null); setAddModal(true) }}
            sx={{ background: 'linear-gradient(135deg,#f97316,#ea580c)', color: '#fff', fontWeight: 700, px: 3, py: 1.2, borderRadius: '12px', boxShadow: '0 4px 16px rgba(249,115,22,0.45)', textTransform: 'none', '&:hover': { background: 'linear-gradient(135deg,#ea580c,#c2410c)', transform: 'translateY(-1px)' }, transition: 'all 0.2s ease' }}>
            Add Coupon
          </Button>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          {[
            { label: 'Total Coupons', value: totalCount, bg: 'linear-gradient(135deg,#fff7ed,#ffedd5)', border: '#fed7aa', color: '#c2410c' },
            { label: 'Active', value: enabledCount, bg: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', border: '#bbf7d0', color: '#166534' },
            { label: 'Inactive', value: totalCount - enabledCount, bg: 'linear-gradient(135deg,#f8fafc,#f1f5f9)', border: '#e2e8f0', color: '#64748b' }
          ].map(s => (
            <Paper key={s.label} elevation={0} sx={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: '16px', px: 3, py: 2, minWidth: 140, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>{s.value}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500, mt: 0.2 }}>{s.label}</Typography>
            </Paper>
          ))}
        </Box>

        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', background: '#fafbfc' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>
              All Coupons <Typography component="span" sx={{ ml: 1, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>({coupons.length})</Typography>
            </Typography>
            <TextField size="small" placeholder="Search coupons..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>, sx: { borderRadius: '10px', fontSize: '0.83rem', bgcolor: '#fff' } }}
              sx={{ width: 260 }} />
          </Box>
          {error && <Box sx={{ px: 3, py: 2, bgcolor: '#fef2f2' }}><Typography sx={{ fontSize: '0.83rem', color: '#dc2626' }}>Error: {error.message}</Typography></Box>}
          {loading ? <CustomLoader /> : (
            <DataTable columns={columns} data={coupons} pagination paginationServer
              paginationTotalRows={totalCount} paginationPerPage={rowsPerPage}
              onChangePage={p => setPage(p - 1)} onChangeRowsPerPage={(rpp, p) => { setRowsPerPage(rpp); setPage(p - 1) }}
              noHeader sortFunction={(rows, field, dir) => orderBy(rows, row => row[field]?.toString().toLowerCase(), dir)}
              customStyles={{ headRow: { style: { backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', minHeight: '44px' } }, headCells: { style: { fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase' } }, rows: { style: { borderBottom: '1px solid #f1f5f9', minHeight: '64px', '&:hover': { backgroundColor: '#f8fafc' } } }, cells: { style: { paddingLeft: '16px', paddingRight: '16px' } } }}
              noDataComponent={<Box py={8} textAlign="center"><LocalOfferIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} /><Typography sx={{ color: '#64748b' }}>No coupons found</Typography></Box>}
            />
          )}
        </Paper>
      </Container>

      <Dialog open={addModal} onClose={() => setAddModal(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2.5, px: 3, pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">Add New Coupon</Typography>
          <IconButton size="small" onClick={() => setAddModal(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3 }}>
          <CouponComponent onClose={() => { setAddModal(false); refetch() }} />
        </DialogContent>
      </Dialog>

      <Dialog open={editModal} onClose={() => setEditModal(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2.5, px: 3, pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">Edit Coupon</Typography>
          <IconButton size="small" onClick={() => setEditModal(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3 }}>
          <CouponComponent coupon={selectedCoupon} onClose={() => { setEditModal(false); refetch() }} />
        </DialogContent>
      </Dialog>
    </>
  )
}

export default withTranslation()(Coupons)